import { createFileRoute } from "@tanstack/react-router"
import { ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { createLLMOMeta, createBreadcrumbSchema, createArticleSchema } from "@/lib/seo"
import { useAuth } from "@/components/auth/AuthProvider"
import { useAnalytics } from "@/lib/analytics"
import { Paragraph, ContentSection } from "@/components/tools/SeoContent"
import { HugeiconsIcon } from "@hugeicons/react"
import ArrowRight02Icon from "@hugeicons/core-free-icons/ArrowRight02Icon"

// ─── Comparison Data ─────────────────────────────────────────
type ComparisonItem = {
  competitor: string
  slug: string
  tagline: string
  keyDifference: string
  zapifyWins: string[]
}

const comparisons: ComparisonItem[] = [
  {
    competitor: "ManyChat",
    slug: "/comparison/manychat-vs-zapify-pro",
    tagline: "ManyChat is expensive and complex. Zapify is built for Instagram creators.",
    keyDifference: "Multi-channel vs Instagram-focused",
    zapifyWins: ["4x cheaper pricing", "No overage fees", "Lifetime access option", "Instagram-specific AI"],
  },
  {
    competitor: "LinkDM",
    slug: "/comparison/linkdm-vs-zapify-pro",
    tagline: "LinkDM caps your DMs and sells credit packages. Zapify gives you unlimited.",
    keyDifference: "DM caps + credits vs unlimited DMs",
    zapifyWins: ["Unlimited DMs on paid plans", "No credit packages", "AI-powered replies", "Growth attribution"],
  },
  {
    competitor: "Zorcha",
    slug: "/comparison/zorcha-vs-zapify-pro",
    tagline: "Zorcha covers core DM automation. Zapify adds AI growth attribution and smart offers.",
    keyDifference: "Basic automation vs growth platform",
    zapifyWins: ["AI brand voice controls", "Growth attribution", "Smart offers & templates", "Lifetime access"],
  },
  {
    competitor: "InstantDM",
    slug: "/comparison/instantdm-vs-zapify-pro",
    tagline: "InstantDM is an operations stack. Zapify is a creator growth platform.",
    keyDifference: "Complex operations vs simple growth",
    zapifyWins: ["30-day free plan (vs 7 days)", "Simpler creator workflow", "Growth attribution", "Lifetime access"],
  },
  {
    competitor: "ReplyRush",
    slug: "/comparison/replyrush-vs-zapify-pro",
    tagline: "ReplyRush caps DMs on every plan. Zapify removes the ceiling entirely.",
    keyDifference: "DM volume caps vs unlimited",
    zapifyWins: ["Unlimited DMs on paid plans", "All features from Starter", "AI replies (vs unavailable)", "Better economics"],
  },
  {
    competitor: "SuperProfile",
    slug: "/comparison/superprofile-vs-zapify-pro",
    tagline: "SuperProfile is a broad creator-commerce suite. Zapify is focused on Instagram growth.",
    keyDifference: "Creator-commerce suite vs focused automation",
    zapifyWins: ["No platform bloat", "Growth attribution", "Transparent flat pricing", "Lifetime access"],
  },
  {
    competitor: "Inrō",
    slug: "/comparison/inro-vs-zapify-pro",
    tagline: "Inrō uses contact-metered billing. Zapify gives you flat pricing and unlimited DMs.",
    keyDifference: "Contact-metered vs flat pricing",
    zapifyWins: ["Flat predictable pricing", "Unlimited DMs", "Creator growth dashboard", "Lifetime access"],
  },
  {
    competitor: "LinktoDM",
    slug: "/comparison/linktodm-vs-zapify-pro",
    tagline: "LinktoDM covers basics. Zapify adds AI, growth attribution, and lifetime access.",
    keyDifference: "Basic automation vs growth platform",
    zapifyWins: ["AI-powered replies", "Growth attribution", "Smart offers & templates", "Lifetime access"],
  },
  {
    competitor: "CreatorFlow",
    slug: "/comparison/creatorflow-vs-zapify-pro",
    tagline: "CreatorFlow is solid but pricier. Zapify delivers more for less with lifetime access.",
    keyDifference: "Higher cost vs better value",
    zapifyWins: ["Cheaper entry pricing", "Lifetime access option", "More automation types", "Growth attribution"],
  },
]

// ─── Route ───────────────────────────────────────────────────
export const Route = createFileRoute("/comparison/")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: "Zapify vs Competitors: Instagram DM Automation Comparisons",
        description:
          "Compare Zapify with ManyChat, LinkDM, Zorcha, InstantDM, ReplyRush, SuperProfile, Inrō, LinktoDM, and CreatorFlow for Instagram DM automation, AI replies, bulk DMs, pricing, and creator growth.",
        url: "https://zapify.pro/comparison",
        publishedTime: "2026-06-06",
        modifiedTime: "2026-06-06",
      }),
      { property: "og:image", content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png" },
      { property: "og:locale", content: "en_US" },
      { property: "og:type", content: "website" },
      { name: "keywords", content: "Zapify comparison, ManyChat alternative, LinkDM alternative, Zorcha alternative, InstantDM alternative, ReplyRush alternative, SuperProfile alternative, Inro alternative, LinktoDM alternative, CreatorFlow alternative, Instagram DM automation, AI Instagram replies" },
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/comparison" },
    ],
    scripts: [
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: "Home", item: "https://zapify.pro" },
          { position: 2, name: "Comparisons", item: "https://zapify.pro/comparison" },
        ],
      }),
    ],
  }),
  component: ComparisonIndexPage,
})

// ─── Page Component ──────────────────────────────────────────
export function ComparisonIndexPage() {
  const { track } = useAnalytics()
  const { openAuth } = useAuth()

  const handleCTA = (ctaId: string, buttonText: string) => {
    track("cta_clicked", { cta_id: ctaId, button_text: buttonText })
    openAuth()
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* Radial fade background */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      {/* Dotted background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808015_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 pt-28 sm:pt-36 pb-12">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 shadow-sm mb-6">
            <span className="text-neutral-600 dark:text-neutral-300 font-semibold text-xs">Updated June 2026</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight max-w-5xl mx-auto">
            Zapify vs{" "}
            <span className="gradient-text">The Competition</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-neutral-600 dark:text-neutral-400 font-medium max-w-3xl mx-auto mb-8 sm:mb-10">
            See how Zapify compares to every major Instagram DM automation tool. AI-powered replies, unlimited DMs, growth attribution, and lifetime access — all in one platform built for creators.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button
              onClick={() => handleCTA("CTA_Comparison_Index_Hero", "Start Free 30 Days")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-8 py-6 text-base rounded-full shadow-lg"
            >
              Start Free 30 Days
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-4 text-center">
              <p className="text-2xl sm:text-3xl font-black text-green-600 dark:text-green-400">$0</p>
              <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-1">30-day free plan</p>
            </div>
            <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-4 text-center">
              <p className="text-2xl sm:text-3xl font-black text-green-600 dark:text-green-400">∞</p>
              <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-1">Unlimited DMs</p>
            </div>
            <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-4 text-center">
              <p className="text-2xl sm:text-3xl font-black text-green-600 dark:text-green-400">$200</p>
              <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-1">Lifetime access</p>
            </div>
            <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-4 text-center">
              <p className="text-2xl sm:text-3xl font-black text-green-600 dark:text-green-400">AI</p>
              <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-1">Brand voice replies</p>
            </div>
          </div>
        </section>

        {/* Comparison Cards */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              Choose a <span className="gradient-text">comparison</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              Detailed, transparent side-by-side breakdowns of features, pricing, and capabilities.
            </Paragraph>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {comparisons.map((item) => (
              <a
                key={item.slug}
                href={item.slug}
                className="group rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-5 sm:p-6 hover:border-green-300 dark:hover:border-green-700 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-white">
                    Zapify vs {item.competitor}
                  </h3>
                  <HugeiconsIcon
                    icon={ArrowRight02Icon}
                    size={18}
                    color="currentColor"
                    className="text-neutral-400 group-hover:text-green-600 dark:group-hover:text-green-400 transition-all group-hover:translate-x-0.5"
                  />
                </div>

                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">
                  {item.tagline}
                </p>

                <div className="mb-4">
                  <span className="inline-flex items-center rounded-full bg-neutral-100 dark:bg-neutral-800 px-2.5 py-1 text-[11px] font-bold text-neutral-600 dark:text-neutral-300">
                    {item.keyDifference}
                  </span>
                </div>

                <div className="space-y-1.5">
                  {item.zapifyWins.map((win) => (
                    <div key={win} className="flex items-center gap-2">
                      <span className="inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                        <svg className="w-2.5 h-2.5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span className="text-xs text-neutral-600 dark:text-neutral-400">{win}</span>
                    </div>
                  ))}
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Why Zapify Section */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <article className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white mb-4 tracking-tight text-center">
              Why creators choose <span className="gradient-text">Zapify</span>
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">AI-powered replies with brand voice controls</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">Unlimited DMs on paid plans — no caps</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">Zapify insights: followers + leads gained</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">Smart offers + templates for fast launches</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">$0 free plan for 30 days or 1K contacts</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">$200 lifetime access — no recurring fees</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">10+ language support for global creators</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">Ask-to-follow + email capture in DMs</span>
                </div>
              </div>
            </div>
          </article>
        </section>

        {/* Bottom CTA */}
        <section className="relative overflow-hidden py-16 sm:py-24 px-4 sm:px-6">
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight">
              Ready to grow <span className="gradient-text">smarter?</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 mb-8 sm:mb-10 max-w-xl mx-auto">
              Start free for 30 days. Launch your first automation in minutes. See why creators switch to Zapify.
            </p>
            <Button
              onClick={() => handleCTA("CTA_Comparison_Index_Bottom", "Start Free with Zapify")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-10 py-6 text-lg rounded-full shadow-lg"
            >
              Start Free with Zapify
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              30-day free plan &middot; No credit card &middot; 1-minute setup
            </p>
          </div>
        </section>

        {/* Disclaimer */}
        <ContentSection className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
          <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center">
            All competitor names are trademarks of their respective owners. Zapify is not affiliated with, endorsed by, or sponsored by any competitor listed. Feature and pricing information is based on publicly available information as of June 2026. Always check each provider's website for current details.
          </p>
        </ContentSection>
      </div>
    </div>
  )
}
